import React, { useState, useEffect, useRef } from 'react';
import { img_path} from '../../../environment';

interface Image {
  className?: string;
  src: string;
  alt?: string;
  height?: number;
  width?: number;
  id?: string;
  style?: React.CSSProperties;
  loading?: 'lazy' | 'eager';
  placeholder?: string;
}

const ImageWithBasePath = (props: Image) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Combine the base path and the provided src to create the full image source URL
  const fullSrc = `${img_path}${props.src}`;
  const loading = props.loading ?? 'lazy';

  useEffect(() => {
    // If eager loading or already loaded, skip intersection observer
    if (loading === 'eager' || isLoaded) {
      return;
    }

    // Use Intersection Observer for lazy loading
    if ('IntersectionObserver' in window && imgRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && imgRef.current) {
              // Start loading the image
              const img = new window.Image();
              img.src = fullSrc;
              img.onload = () => setIsLoaded(true);
              img.onerror = () => setHasError(true);
              
              if (observerRef.current && imgRef.current) {
                observerRef.current.unobserve(imgRef.current);
              }
            }
          });
        },
        { rootMargin: '50px' } // Start loading 50px before image enters viewport
      );

      if (imgRef.current) {
        observerRef.current.observe(imgRef.current);
      }
    } else {
      // Fallback for browsers without IntersectionObserver
      setIsLoaded(true);
    }

    return () => {
      if (observerRef.current && imgRef.current) {
        observerRef.current.unobserve(imgRef.current);
      }
    };
  }, [fullSrc, loading, isLoaded]);

  // Ensure width and height are set for CLS prevention
  const imageStyle: React.CSSProperties = {
    ...props.style,
    ...(props.width && props.height ? {
      aspectRatio: `${props.width} / ${props.height}`,
    } : {}),
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {!isLoaded && !hasError && loading === 'lazy' && (
        <div
          style={{
            width: props.width || '100%',
            height: props.height || 'auto',
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: props.height ? `${props.height}px` : '200px',
          }}
          aria-hidden="true"
        >
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid #ddd',
            borderTopColor: '#506EE4',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      )}
      <img
        ref={imgRef}
        className={props.className}
        src={isLoaded || loading === 'eager' ? fullSrc : props.placeholder || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3C/svg%3E'}
        height={props.height}
        alt={props.alt || ''}
        width={props.width}
        id={props.id}
        style={{
          ...imageStyle,
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          display: hasError ? 'none' : 'block',
        }}
        loading={loading}
        decoding="async"
        onLoad={() => setIsLoaded(true)}
        onError={() => setHasError(true)}
      />
      {hasError && (
        <div
          style={{
            width: props.width || '100%',
            height: props.height || '200px',
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '14px',
          }}
          role="img"
          aria-label={props.alt || 'Image failed to load'}
        >
          {props.alt || 'Image not available'}
        </div>
      )}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ImageWithBasePath;
